import ItemMenuSquares, { _imsTypes } from 'global/wrappers/ItemMenuSquares'
import { useSportsFilters } from 'projectSpecific/sports/useHelpers/useSportsFilters'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import _ from 'lodash';
import { useBreadcrumbs } from 'global/cnr/contexts/useHelpers/useBreadcrumbs';
import Wrapper, { wrapperTypes } from 'global/wrappers/Wrapper';
import { Icon, Label } from 'semantic-ui-react';
import { CreateSportsSeasonContext } from 'projectSpecific/sports/create/CreateSeason';
import { SportsSeasonContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonContext';
import { sortingHelpers } from 'global/common/sorting';
import { _matchDisplayTypes, MatchContentWithProvider } from './MatchContent';
import { gEnums } from 'global/enums/globalEnums';
import { _matchFoundTypes } from 'projectSpecific/sports/cnr/reducers/reducerHelpers/seasonSportsMatchData';
import TeamSchedule from '../schedule/TeamSchedule';

const _matchSquareTypes = {
  base: 'base',
  levels: 'levels',
  matchesByLevel: 'matchesByLevel',
  playoffMatchesByLevel: 'playoffMatchesByLevel',
  playoffMatches: 'playoffMatches',
  match: 'match',
  teams: 'teams',
  teamsDirect: 'teamsDirect',
  matchListByTeam: 'matchListByTeam',
  startDate: 'startDate',
}

// menuItems for base
const _base = {
  startDate: 'startDate',
  teams: 'teams',
  levels: 'levels',
}

export const matchStatusColors = {
  new: 'green',
  changed: 'yellow',
  scoreChanged: 'yellow',
  removed: 'red',
  duplicated: 'purple',
  cancelled: 'pink',
  matchIsNew: 'green',
  dateChanged: 'orange',
  scoreIsNew: 'blue',
}

export const _msTypes = {
  date: 'date',
  base: 'base',
  teams: 'teams',
  playoffs: 'playoffs'
}

const MatchSquares = ({ matches, msType, showChanges, forPlayoffs, fromLookup }) => {

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { teams_info, matches_info } = sportsSeason_state ?? {}
  const { teams: teams_season } = teams_info ?? {}
  const { matches: matches_season } = matches_info ?? {}

  const createSportsSeasonContext = useContext(CreateSportsSeasonContext);
  const { createSportsSeason_state } = createSportsSeasonContext ?? {}
  const { sportsSeason_selected } = createSportsSeason_state ?? {}
  const { seasonals } = sportsSeason_selected ?? {}
  const { teams: teams_seasonal } = seasonals ?? {}

  const forCreate = createSportsSeason_state ? true : false

  const teams = teams_seasonal || teams_season

  const { getLevelMatches } = useSportsFilters()

  const [currentMatchGroups, setCurrentMatchGroups] = useState({ startDate: {}, teams: {}, levels: {} })

  const [allGroups, setAllGroups] = useState([])
  const currentGroup = allGroups?.[allGroups.length - 1]

  const backLinks = useMemo(() => {
    const bl = _.map(
      allGroups.slice(0, -1),
      (group, index) => (
        {
          key: group.key || 0,
          caption: group.caption
        }
      )
    );
    return bl
  }, [allGroups])

  const goBack = () => {
    const _allGroups = _.clone(allGroups)
    if (_allGroups.length <= 1) { return; }
    const all_groups = _allGroups.slice(0, -1)
    setAllGroups(all_groups)
  }

  const goBackTo = (index) => {
    const _allGroups = _.clone(allGroups)
    if (index < 0 || index >= _allGroups.length) return;
    if (_allGroups.length <= 1) return; // ⛔ root protected 
    const all_groupz = _allGroups.slice(0, index + 1)
    setAllGroups(all_groupz)
  }

  const addToGroup = (group) => {
    const _allGroups = _.clone(allGroups)
    _allGroups.push(group)
    setAllGroups(_allGroups)
  }

  const levelKeys = (lms) => {
    const lmks = _.keys(lms)

    return _.orderBy(
      lmks,
      [
        (key) => _.startsWith(_.toLower(key), 'non') ? 1 : 0, // push "non" to end
        (key) => {
          const match = key.match(/\d+/)
          return match ? parseInt(match[0], 10) : -1
        }
      ],
      ['asc', 'desc'] // non last, numbers high → low
    )
  }

  const bcrb = useBreadcrumbs({ items: backLinks, onClick: goBackTo })

  const groupByTeam = (mz) => {
    const groupedByTeam = {};
    _.forEach(mz, (match) => {
      const homeName = match?.home?.name;
      const awayName = match?.away?.name;

      if (homeName) {
        if (!groupedByTeam[homeName]) {
          groupedByTeam[homeName] = [];
        }
        groupedByTeam[homeName].push(match);
      }

      if (awayName) {
        if (!groupedByTeam[awayName]) {
          groupedByTeam[awayName] = [];
        }
        groupedByTeam[awayName].push(match);
      }
    });
    return groupedByTeam
  }

  useEffect(() => {
    if (matches) {
      const matches_playoffs = _.filter(matches, { isPlayoff: true })
      const { levelMatches } = getLevelMatches({ matches })
      const { levelMatches: levelMatches_playoffs } = getLevelMatches({ matches: matches_playoffs })
      const dateMatches = _.groupBy(matches, 'startDate')

      const _currentMatchGroups = {
        startDate: _.groupBy(matches, 'startDate'),
        levels: _.groupBy(matches, '_matchCollectionKey'),
        teams: groupByTeam(matches),
        playoffs: _.groupBy(matches_playoffs, 'poolKey'),
        playoffLevels: levelMatches_playoffs,
      }

      setCurrentMatchGroups(_currentMatchGroups)

      const _allGroups = []

      switch (msType) {
        case _msTypes.base:
          const mis_base = _.keys(_base)
          _allGroups.push({
            key: _matchSquareTypes.base,
            value: '',
            caption: 'Matches by Group',
            menuItems: mis_base
          })
          break;

        case _msTypes.date:
          const mis_date = sortingHelpers.sortDates(_.keys(dateMatches))
          _allGroups.push({
            key: _matchSquareTypes.startDate,
            value: '',
            caption: 'Matches',
            menuItems: mis_date
          })
          break;

        case _msTypes.teams:
          const mis_teams = _.map(teams, 'name');
          _allGroups.push({
            key: _matchSquareTypes.teams,
            value: '',
            caption: 'Teams',
            menuItems: _.sortBy(mis_teams)
          })
          break;

        case _msTypes.playoffs:
          _allGroups.push({
            key: _matchSquareTypes.matchesByLevel,
            value: '',
            caption: 'Playoffs',
            menuItems: matches
          })
          break;

        default:
          if (forPlayoffs || !_.isEmpty(levelMatches_playoffs)) {
            _allGroups.push({
              key: _matchSquareTypes.playoffMatchesByLevel,
              value: '',
              caption: 'Playoff Matches',
              menuItems: levelKeys(levelMatches_playoffs),
              count: _.size(matches_playoffs)
            })
          } else {
            _allGroups.push({
              key: _matchSquareTypes.matchesByLevel,
              value: '',
              caption: 'Matches',
              menuItems: levelKeys(levelMatches),
              count: _.size(matches)
            })
          }

      }
      setAllGroups(_allGroups)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [matches, forPlayoffs]);

  const onClick_date = (date) => {
    const matches_level = currentMatchGroups.startDate?.[date]
    addToGroup({ key: _matchSquareTypes.matchListByTeam, value: date, caption: date, menuItems: sortingHelpers.sortByStartDate(matches_level) })
  }

  const onClick_base = (baseType) => {
    switch (baseType) {
      case _base.levels:
        const levelMatches = _.groupBy(matches, '_matchCollectionKey')
        addToGroup({ key: _matchSquareTypes.matchesByLevel, value: '', caption: 'Matches', menuItems: levelKeys(levelMatches), count: _.size(matches) })
        break;
      case _base.teams:
        const mis_t = _.keys(currentMatchGroups?.[baseType])
        addToGroup({ key: baseType, value: baseType, caption: _.startCase(baseType), menuItems: _.sortBy(mis_t) })
        break;
      case _base.startDate:
        const mis_d = _.keys(currentMatchGroups?.[baseType])
        addToGroup({ key: _matchSquareTypes.startDate, value: baseType, caption: _.startCase(baseType), menuItems: _.sortBy(mis_d) })
        break;
    }
  }

  const onClick_level = (level) => {
    const matches_level = currentMatchGroups.levels?.[level]
    const sectionMatches = _.groupBy(matches_level, 'sections')
    const _sectionKeys = _.map(sectionMatches, (s, sk) => {
      let _sk;
      switch (sk) {
        case 'Non':
          _sk = sk
          break;
        default:
          _sk = sk.includes('section') ? sk : `section${sk}`
      }
      return _sk
    })
    addToGroup({ key: _matchSquareTypes.levels, value: level, caption: level, menuItems: _.sortBy(_sectionKeys), contents: sectionMatches })
  }

  const onClick_playoffLevel = (level) => {
    const matches_level = currentMatchGroups.playoffLevels?.[level]
    const _levelKeys = _.keys(matches_level)
    const _content = sortingHelpers.sortByStartDate(matches_level)
    addToGroup({ key: _matchSquareTypes.playoffMatches, value: level, caption: level, menuItems: matches_level })
  }

  const onClick_section = (section) => {
    const _section = forCreate ? section.replace('section', '') : section
    const cl = _.find(allGroups, { key: _matchSquareTypes.levels })
    const matches_level = msType === _msTypes.date ? currentMatchGroups.startDate?.[cl?.value] : currentMatchGroups.levels?.[cl?.value]
    const sectionMatches = _.groupBy(matches_level, 'sections')
    const menuItems = sectionMatches?.[_section]
    const _content = sortingHelpers.sortByStartDate(menuItems)
    addToGroup({ key: _matchSquareTypes.matchListByTeam, value: section, caption: `${_.startCase(section)}`, menuItems: _content })
  }

  const onClick_team = (team) => {
    const { name } = team ?? {}
    const t = name || team
    const matches_team = currentMatchGroups.teams?.[t]
    const _content = sortingHelpers.sortByStartDate(matches_team)
    addToGroup({ key: _matchSquareTypes.matchListByTeam, value: t, caption: t, menuItems: _content })
  }

  const onClick_match = (m) => {
    return;
    const _allGroups = _.clone(allGroups)
    _allGroups.push({
      key: _matchSquareTypes.match,
      value: m,
      caption: m.key,
      menuItems: m
    })
    setAllGroups(_allGroups)
  }

  const getCounts = (ms, lbls) => {
    const counts = _.reduce(ms, (acc, match) => {
      const changes = match._matchChanges || {};
      _.forEach(changes, (val, key) => {
        if (val) {
          acc[key] = (acc[key] || 0) + 1;
        }
      });
      return acc;
    }, {});
    _.forEach(counts, (count, ck) => {
      let color = 'black'
      let iconName = 'cog'
      switch (ck) {
        case _matchFoundTypes.scoreChanged:
          color = matchStatusColors.scoreChanged
          iconName = 'redo'
          break;
        case _matchFoundTypes.scoreIsNew:
          color = matchStatusColors.scoreIsNew
          iconName = 'check circle outline'
          break;
        case _matchFoundTypes.cancelled:
          color = matchStatusColors.cancelled
          iconName = 'ban'
          break;
        case _matchFoundTypes.removed:
          color = matchStatusColors.removed
          iconName = 'ban'
          break;
        case _matchFoundTypes.dateChanged:
          color = matchStatusColors.dateChanged
          iconName = 'calendar check outline'
          break;
        case _matchFoundTypes.matchIsNew:
          color = matchStatusColors.matchIsNew
          iconName = 'check circle outline'
          break;
        case _matchFoundTypes.duplicated:
          color = matchStatusColors.duplicated
          iconName = 'copy'
          break;
        default:
          console.log('ck', ck)
      }
      lbls.unshift(<Label size='tiny' title={_.startCase(ck)} color={color}><Icon name={iconName} />{count}</Label>)
    })
    return lbls
  }

  const altContent_level = (item) => {
    const ms = currentMatchGroups.levels?.[item]
    const lbls = [<Label size='tiny' color='black'>{_.size(ms)}</Label>]
    if (showChanges) { getCounts(ms, lbls) }
    return lbls
  }

  const altContent_teams = (team) => {
    const { name } = team ?? {}
    const ms = currentMatchGroups.teams?.[name || team]
    return <Label size='tiny' color='blue'>{_.size(ms)}</Label>
  }

  const altContent_startDate = (startDate) => {
    const ms = currentMatchGroups.startDate?.[startDate]
    return <Label size='tiny' color='blue'>{_.size(ms)}</Label>
  }

  const altContent_section = (item) => {
    const ms = currentGroup?.contents?.[item]
    const lbls = [<Label size='tiny' color='black'>{_.size(ms)}</Label>]
    if (showChanges) { getCounts(ms, lbls) }
    return lbls
  }

  const fn_subContent = (item) => {
    const { startDate, startTime, _results, _oldStartDate, levels, sections } = item ?? {}
    const { scoreDisplays, score } = _results ?? {}
    const { normal } = scoreDisplays ?? {}
    const keyy = allGroups?.[1]?.key
    const labels = []
    if (levels && sections && (keyy === _base.startDate || keyy === _base.teams)) {
      labels.push(<Label style={{ marginTop: '.5em', float: 'right' }} size='tiny' color='black'>{`${levels} - ${_.startCase(sections)}`}</Label>)
    }
    if (_oldStartDate) {
      return <div>
        <div>
          <span>{`${startDate} - ${startTime}`}</span>
          <span style={{ color: 'red', marginLeft: '1em' }} >{_oldStartDate}</span>
        </div>
        {!_.isEmpty(score) && <Label style={{ marginTop: '.5em' }} size='tiny' color='blue'>{normal}</Label>}
        {!_.isEmpty(labels) && labels}
      </div>
    } else {
      return <div>
        <div>{`${startDate} - ${startTime} `}</div>
        {!_.isEmpty(score) && <Label style={{ marginTop: '.5em' }} size='tiny' color='blue'>{normal}</Label>}
        {!_.isEmpty(labels) && labels}
      </div>
    }
  }
  const altContent_match = (item) => {
    const size = 'large'
    const { _matchChanges, startTime, _itemKey } = item ?? {}
    const { dateChanged, matchStatusChanged, scoreChanged, scoreIsNew, matchIsNew, cancelled, removed, duplicated } = _matchChanges ?? {}
    const _match_season = matches_season?.[_itemKey]
    const icons = []
    if (_match_season) { icons.push(<Icon name='check outline circle' color={'green'} size={size} title={'Has Match'} />) }
    if (dateChanged) { icons.push(<Icon name='calendar outline' color={matchStatusColors.dateChanged} size={size} title={'Date Changed'} />) }
    if (matchStatusChanged) { icons.push(<Icon color={matchStatusColors.changed} size={size} name='check circle outline' />) }
    if (scoreChanged) { icons.push(<Icon color={matchStatusColors.changed} size={size} name='numbered list' title={'Score Changed'} />) }
    if (scoreIsNew) { icons.push(<Icon color={matchStatusColors.new} size={size} name='numbered list' title='Score New' />) }
    if (matchIsNew) { icons.push(<Icon name='plus square outline' size={size} color={matchStatusColors.new} title='Match New' />) }
    if (cancelled) { icons.push(<Icon name='ban' size={size} color={matchStatusColors.cancelled} title='Match Cancelled' />) }
    if (removed) { icons.push(<Icon name='delete' size={size} color={matchStatusColors.removed} title='Match Removed' />) }
    if (duplicated) { icons.push(<Icon name='copy' size={size} color={matchStatusColors.duplicated} title='Match Duplicated' />) }

    return !_.isEmpty(icons) ? <div>{icons}</div> : <div>{startTime}</div>
  }

  const squares_base = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_base} />
  const squares_dates = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_date} iconItem={'calendar outline'} fn_altContent={altContent_startDate} ignoreStartCase={true} />
  const squares_levels = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_level} fn_altContent={altContent_level} />
  const squares_playoffLevels = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_playoffLevel} fn_altContent={altContent_level} />
  const squares_playoffMatches = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.matchFull} fn_altContent={altContent_match} />
  const squares_match = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.match} onClick={onClick_match} fn_altContent={altContent_match} fn_subContent={fn_subContent} />
  // const squares_match_single = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_match} fn_altContent={altContent_match} />
  const squares_sections = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_section} fn_altContent={altContent_section} />
  const squares_teams = (menuItems) => <ItemMenuSquares menuItems={menuItems} imsType={_imsTypes.inline} onClick={onClick_team} iconItem={'users'} fn_altContent={altContent_teams} />

  const match_single = (m) => {
    const propz = {
      matchDisplayType: _matchDisplayTypes.levelMatches,
      matchKey: m._itemKey,
      matchScheduleDisplayType: gEnums.matchScheduleDisplayTypes.levelDates
    }
    return <MatchContentWithProvider {...propz} />
  }

  const squares_matchList_byTeams = (menuItems) => {
    switch (msType) {
      case _msTypes.teams:
        const _team = _.find(teams, { name: currentGroup.caption })
        return <TeamSchedule
          fromSquares={true}
          teamKey={_team?._itemKey}
        />
      default:
        // return <LevelSchedule />

        const items = []
        if (forCreate) {
          const groupedTeams = _.groupBy(teams, 'levels');
          const levels = _.mapValues(groupedTeams, (teamsInLevel) =>
            _.groupBy(teamsInLevel, 'sections')
          );
          const sl = {
            level: allGroups?.[1].value,
            section: allGroups?.[2].value
          }
          const t = levels?.[sl.level]?.[sl.section]
          items.push(squares_teams(_.sortBy(t, 'position')))
        }
        items.push(squares_match(menuItems))

        return items
    }

  }

  const header_top = () => {
    const currentGroup = allGroups?.[allGroups.length - 1]
    const { caption, key } = currentGroup ?? {}
    const _caption = caption || _.startCase(key)
    return <div className='swr-header'>
      {bcrb}
      <div className='swr-header-cap'>
        {allGroups.length > 1 && <Icon color={'blue'} name='arrow left' onClick={(e) => goBack()}></Icon>}
        {_caption}</div>
    </div>
  }

  const content = () => {
    if (allGroups) {
      const { key, menuItems } = currentGroup ?? {}
      switch (key) {
        case _matchSquareTypes.base:
          return squares_base(menuItems)
        case _matchSquareTypes.startDate:
          return squares_dates(menuItems)
        case _matchSquareTypes.matchesByLevel:
          return squares_levels(menuItems, currentGroup)
        case _matchSquareTypes.playoffMatchesByLevel:
          return squares_playoffLevels(menuItems, currentGroup)
        case _matchSquareTypes.playoffMatches:
          return squares_playoffMatches(menuItems, currentGroup)
        case _matchSquareTypes.levels:
          return squares_sections(menuItems)
        case _matchSquareTypes.matchListByTeam:
          return squares_matchList_byTeams(menuItems, currentGroup)
        case _matchSquareTypes.teams:
          return squares_teams(menuItems)
        case _matchSquareTypes.match:
          return match_single(menuItems, currentGroup)
      }
    }
  }

  return (
    <Wrapper
      header={header_top()}
      content={content()}
      wrapperType={wrapperTypes.padded}
      noMid={fromLookup ? false : true}
    ></Wrapper>
  )
}

export default MatchSquares